import {
  Datepicker,
  FormItem,
  UsersPicker,
  Checkbox,
} from '@getsynapse/design-system';
import { useFlags } from 'launchdarkly-react-client-sdk';
import MultiSelectDropdown from 'Organisms/MultiSelectDropdow/MultiSelectDropdown';
import useProgramFilters from 'Pages/ProgramsListPage/hooks/useProgramFilters';
import intl from 'react-intl-universal';
import { PROGRAM_TABLE_FILTERS } from 'utils/constants/program';
import { Option, rangeDateType } from 'utils/customTypes';
import {
  getInitialDatePickerValue,
  getInitialUsersPickerValue,
  getInitialValueForDropDown,
} from 'utils/functions';
import { isArrayOfOptions, isDateRangeObject } from 'utils/typeGuards';
import { ProgramFilters, RangeFilter } from 'utils/types/filters';

interface FiltersFormProps {
  appliedFilters: ProgramFilters;
  onUpdateFilters: (newFilters: ProgramFilters) => void;
}

type UpdateFiltersCallback = (
  key: string,
  value: Option[] | rangeDateType | string | boolean,
  rangeFilterKey?: 'from' | 'to'
) => void;

const FiltersForm: React.FC<FiltersFormProps> = ({
  appliedFilters,
  onUpdateFilters,
}) => {
  const { statusOptions, deliveryOptions, ownerOptions } = useProgramFilters();
  const { archivePrograms = false } = useFlags();

  const updateFilters: UpdateFiltersCallback = (key, value, rangeFilterKey) => {
    const updatedFilters: ProgramFilters = { ...appliedFilters };
    if (isArrayOfOptions(value)) {
      if (value.length === 0) {
        delete updatedFilters[key];
      } else {
        updatedFilters[key] = value.map((option) => option.value);
      }
    } else if (isDateRangeObject(value) && rangeFilterKey) {
      updatedFilters[key] = {
        ...(updatedFilters[key] as RangeFilter),
        [rangeFilterKey]: value.startDate ? value.startDate.toString() : null,
      };
      if (
        (updatedFilters[key] as RangeFilter).from === null &&
        (updatedFilters[key] as RangeFilter).to === null
      ) {
        delete updatedFilters[key];
      }
    } else if (typeof value === 'boolean') {
      if (value) {
        updatedFilters[key] = value;
      } else {
        delete updatedFilters[key];
      }
    }
    onUpdateFilters(updatedFilters);
  };

  return (
    <div className='mt-8 mb-12 flex flex-col space-y-5'>
      {archivePrograms && (
        <Checkbox
          label={intl.get(
            'PROGRAMS_LIST_PAGE.ARCHIVED_PROGRAMS.VIEW_ARCHIVED_PROGRAMS'
          )}
          onChange={(event) =>
            updateFilters(PROGRAM_TABLE_FILTERS.ARCHIVED, event.target.checked)
          }
          checked={
            (appliedFilters[PROGRAM_TABLE_FILTERS.ARCHIVED] as boolean) ?? false
          }
          data-testid='programs-filters__archived-checkbox'
        />
      )}
      <FormItem label={intl.get('PROGRAMS_LIST_PAGE.TABLE.COLUMNS.STATUS')}>
        <MultiSelectDropdown
          options={statusOptions}
          values={getInitialValueForDropDown(
            statusOptions,
            appliedFilters[PROGRAM_TABLE_FILTERS.STATUS] as string[]
          )}
          onChange={(options: Option[]) =>
            updateFilters(PROGRAM_TABLE_FILTERS.STATUS, options)
          }
          triggerProps={{
            'aria-label': intl.get('PROGRAMS_LIST_PAGE.TABLE.COLUMNS.STATUS'),
            'data-testid': 'programs-filters__status-picker',
          }}
          listProps={{
            'data-testid': 'programs-filters__status__options-list',
          }}
          placeholder={intl.get('SELECT')}
        />
      </FormItem>

      <FormItem label={intl.get('PROGRAMS_LIST_PAGE.TABLE.COLUMNS.OWNERS')}>
        <UsersPicker
          deactivatedUserText={intl.get('DEACTIVATED')}
          usersList={ownerOptions}
          onChange={(users) =>
            updateFilters(PROGRAM_TABLE_FILTERS.OWNERS, users)
          }
          selectedUsersList={getInitialUsersPickerValue(
            ownerOptions,
            appliedFilters[PROGRAM_TABLE_FILTERS.OWNERS] as string[]
          )}
          triggerText={intl.get('FILTER_GENERAL.PLACEHOLDER')}
          triggerProps={{
            'aria-label': intl.get('PROGRAMS_LIST_PAGE.TABLE.COLUMNS.OWNERS'),
            'data-testid': 'programs-filters__owner-picker',
          }}
          popperProps={{
            'data-testid': 'programs-filters__owner__options-list',
          }}
        />
      </FormItem>

      <FormItem
        label={intl.get('PROGRAMS_LIST_PAGE.TABLE.COLUMNS.DELIVERY_TYPE')}
      >
        <MultiSelectDropdown
          options={deliveryOptions}
          values={getInitialValueForDropDown(
            deliveryOptions,
            appliedFilters[PROGRAM_TABLE_FILTERS.DELIVERY] as string[]
          )}
          onChange={(options: Option[]) =>
            updateFilters(PROGRAM_TABLE_FILTERS.DELIVERY, options)
          }
          triggerProps={{
            'aria-label': intl.get(
              'PROGRAMS_LIST_PAGE.TABLE.COLUMNS.DELIVERY_TYPE'
            ),
            'data-testid': 'programs-filters__delivery-picker',
          }}
          listProps={{
            'data-testid': 'programs-filters__delivery__options-list',
          }}
          placeholder={intl.get('SELECT')}
        />
      </FormItem>

      <FormItem label={intl.get('PROGRAMS_LIST_PAGE.TABLE.COLUMNS.CREATION')}>
        <div className='w-full flex space-x-4'>
          <Datepicker
            className='w-full'
            startPlaceHolder={intl.get('FILTER_GENERAL.FROM')}
            startDate={getInitialDatePickerValue(
              'from',
              appliedFilters[PROGRAM_TABLE_FILTERS.CREATION] as RangeFilter
            )}
            onPickDate={(date: rangeDateType) =>
              updateFilters(PROGRAM_TABLE_FILTERS.CREATION, date, 'from')
            }
            inputProps={{
              'aria-label': `${intl.get(
                'PROGRAMS_LIST_PAGE.TABLE.COLUMNS.CREATION'
              )}__from-range`,
              'data-testid': 'programs-filters__creation-date__from-range',
            }}
          />
          <Datepicker
            className='w-full'
            startPlaceHolder={intl.get('FILTER_GENERAL.TO')}
            startDate={getInitialDatePickerValue(
              'to',
              appliedFilters[PROGRAM_TABLE_FILTERS.CREATION] as RangeFilter
            )}
            onPickDate={(date: rangeDateType) =>
              updateFilters(PROGRAM_TABLE_FILTERS.CREATION, date, 'to')
            }
            inputProps={{
              'aria-label': `${intl.get(
                'PROGRAMS_LIST_PAGE.TABLE.COLUMNS.CREATION'
              )}__to-range`,
              'data-testid': 'programs-filters__creation-date__to-range',
            }}
          />
        </div>
      </FormItem>
    </div>
  );
};

export default FiltersForm;
